
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Drawer,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  Typography,
  Box,
  Chip,
  Stack,
  Divider,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ImageUploadPreview from '../ui/ImageUpload/ImageUploadPreview';
import { uploadImage } from '../../api/hotelinfoApi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const slugify = (s = '') => s.toString().toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');

const ACTION_DESTINATIONS = [
  { label: 'Chat with Concierge', href: '/chat' },
  { label: 'Browse Tours', href: '/tours' },
  { label: 'Explore Spa Services', href: '/services/spa' },
  { label: 'In-Room Dining', href: '/restaurant' },
  { label: 'Hotel Information', href: '/hotelinfo' },
  { label: 'My Orders', href: '/orders' },
  { label: 'Services Hub', href: '/services' },
];

const ExplorePostModal = ({ open, onClose, initialData, onSubmit, editingId }) => {
  const [form, setForm] = useState({
    _id: undefined,
    title: '',
    slug: '',
    summary: '',
    content: '',
    imageUrl: '',
    tags: [],
    status: 'draft',
    isFeatured: false,
    heroEyebrow: '',
    readMinutes: '',
    actions: [],
  });
  const [tagInput, setTagInput] = useState('');
  const [coverUploading, setCoverUploading] = useState(false);

  const handleChange = useCallback((field, value) => {
    const newForm = { ...form, [field]: value };
    if (field === 'title') {
      // only auto-generate slug for brand new entries or when slug empty
      if (!form.slug || !editingId) {
        newForm.slug = slugify(value);
      }
    }
    if (field === 'readMinutes') {
      const numeric = Number(value);
      newForm.readMinutes = Number.isFinite(numeric) && numeric > 0 ? numeric : '';
    }
    setForm(newForm);
  }, [form, editingId]);

  const handleAddTag = useCallback(() => {
    const value = tagInput.trim();
    if (!value) return;
    if (form.tags.includes(value)) {
      setTagInput('');
      return;
    }
    setForm((prev) => ({ ...prev, tags: [...(prev.tags || []), value] }));
    setTagInput('');
  }, [tagInput, form.tags]);

  const handleRemoveTag = useCallback((chip) => {
    setForm((prev) => ({ ...prev, tags: (prev.tags || []).filter((tag) => tag !== chip) }));
  }, []);
  const handleAddAction = useCallback(() => {
    const defaultHref = ACTION_DESTINATIONS[0]?.href || '';
    setForm((prev) => ({
      ...prev,
      actions: [...(prev.actions || []), { label: '', href: defaultHref }],
    }));
  }, []);

  const handleRemoveAction = useCallback((index) => {
    setForm((prev) => {
      const next = [...(prev.actions || [])];
      next.splice(index, 1);
      return { ...prev, actions: next };
    });
  }, []);

  const handleActionChange = useCallback((index, field, value) => {
    setForm((prev) => {
      const next = [...(prev.actions || [])];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, actions: next };
    });
  }, []);

  const imageUrls = useMemo(() => (form.imageUrl ? [form.imageUrl] : []), [form.imageUrl]);

  const handleImageUrlsChange = useCallback((urls) => {
    const first = Array.isArray(urls) && urls.length > 0 ? urls[urls.length - 1] : '';
    setForm((prev) => ({ ...prev, imageUrl: first || '' }));
  }, []);

  const sanitizeContent = useCallback((html) => {
    if (typeof window === 'undefined') return html || '';
    const container = window.document.createElement('div');
    container.innerHTML = html || '';

    container.querySelectorAll('p').forEach((node) => {
      const hasMedia = node.querySelector('img, video, iframe, embed');
      if (!hasMedia) {
        const text = node.textContent || '';
        if (!text.trim()) {
          node.remove();
        }
      }
    });

    return container.innerHTML.replace(/\s*<\/p>/g, '</p>');
  }, []);

  useEffect(() => {
    if (!open) return;
    const tags = Array.isArray(initialData?.tags)
      ? initialData.tags
      : (typeof initialData?.tags === 'string' ? initialData.tags.split(',').map((t) => t.trim()).filter(Boolean) : []);
    setForm({
      _id: initialData?._id,
      title: initialData?.title || '',
      slug: initialData?.slug || '',
      summary: initialData?.summary || '',
      content: sanitizeContent(initialData?.content || ''),
      imageUrl: initialData?.imageUrl || '',
      tags,
      status: initialData?.status || 'draft',
      isFeatured: Boolean(initialData?.isFeatured),
      heroEyebrow: initialData?.heroEyebrow || '',
      readMinutes: initialData?.readMinutes || '',
      actions: Array.isArray(initialData?.actions)
        ? initialData.actions.map((action) => ({
            label: action?.label || '',
            href: action?.href || '',
          }))
        : [],
    });
    setTagInput('');
  }, [initialData, open, sanitizeContent]);

  const quillModules = useMemo(() => ({
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ color: [] }, { background: [] }],
      ['blockquote', 'code-block'],
      ['link'],
      ['clean'],
    ],
  }), []);

  const quillFormats = useMemo(() => ([
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'blockquote', 'code-block',
    'link',
  ]), []);

  const handleSubmit = useCallback(() => {
    const payload = {
      ...form,
      tags: form.tags || [],
      actions: (form.actions || []).map((action) => ({
        label: action.label?.trim?.() || '',
        href: action.href?.trim?.() || '',
      })).filter((action) => action.label && action.href),
    };
    if (!payload._id) delete payload._id;
    payload.content = sanitizeContent(payload.content);
    if (payload.readMinutes === '' || payload.readMinutes === null || Number.isNaN(Number(payload.readMinutes))) {
      delete payload.readMinutes;
    } else {
      payload.readMinutes = Number(payload.readMinutes);
    }
    onSubmit(payload);
  }, [form, onSubmit, sanitizeContent]);

  const heroTitle = editingId ? 'Edit Explore Post' : 'Create Explore Post';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 680, maxWidth: '100%' } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #f0f0f0' }}>
          <Typography variant="h6" fontWeight={700}>
            {heroTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Craft a polished story for the Explore hub. Highlight what makes this experience special, add a strong hero image, and use tags so it appears in the right collections.
          </Typography>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 3, pb: 6 }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="overline" sx={{ letterSpacing: 1, color: 'text.secondary' }}>
                Hero Card
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This is the first impression guests see in the Explore feed.
              </Typography>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Title"
                  placeholder="Sunrise at Angkor Wat"
                  value={form.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                />

                <TextField
                  fullWidth
                  label="Hero Eyebrow"
                  placeholder="Sightseeing • 15 min read"
                  helperText="Optional short label shown above the title."
                  value={form.heroEyebrow}
                  onChange={(e) => handleChange('heroEyebrow', e.target.value)}
                />

                <TextField
                  fullWidth
                  label="Slug"
                  value={form.slug}
                  onChange={(e) => handleChange('slug', slugify(e.target.value))}
                  helperText="URL-friendly version of the title. Letters, numbers, and hyphens only."
                />

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Cover Image
                  </Typography>
                  <ImageUploadPreview
                    key={`${editingId || 'new'}-${open ? 'open' : 'closed'}`}
                    initialUrls={imageUrls}
                    onChange={handleImageUrlsChange}
                    uploadImageFn={uploadImage}
                    onUploadingChange={setCoverUploading}
                    maxImages={1}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Use a 3:2 or 4:3 image, minimum 1600px wide for crisp presentation.
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="overline" sx={{ letterSpacing: 1, color: 'text.secondary' }}>
                Story
              </Typography>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Summary"
                  multiline
                  minRows={3}
                  placeholder="Give guests a quick teaser that pulls them into the story."
                  value={form.summary}
                  onChange={(e) => handleChange('summary', e.target.value)}
                  helperText={`${form.summary?.length || 0}/240 characters`}
                  inputProps={{ maxLength: 240 }}
                />

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Body
                  </Typography>
                  <Box
                    sx={{
                      '& .ql-toolbar': {
                        borderTopLeftRadius: 1,
                        borderTopRightRadius: 1,
                        borderColor: '#e0e0e0',
                      },
                      '& .ql-container': {
                        minHeight: 280,
                        borderBottomLeftRadius: 8,
                        borderBottomRightRadius: 8,
                        borderColor: '#e0e0e0',
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                        fontSize: 14,
                      },
                    }}
                  >
                    <ReactQuill
                      theme="snow"
                      value={form.content}
                      onChange={(value) => handleChange('content', value)}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Share details, insider tips, or a suggested itinerary."
                    />
                  </Box>
                </Box>

                <TextField
                  fullWidth
                  label="Estimated Reading Time (minutes)"
                  type="number"
                  value={form.readMinutes}
                  onChange={(e) => handleChange('readMinutes', e.target.value)}
                  InputProps={{ inputProps: { min: 1 } }}
                />
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="overline" sx={{ letterSpacing: 1, color: 'text.secondary' }}>
                Categorisation
              </Typography>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Box>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      fullWidth
                      label="Add Tag"
                      placeholder="Cuisine, Wellness, Off-site..."
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                      helperText="Use short, descriptive tags. Press Enter or tap Add."
                    />
                    <Button
                      variant="outlined"
                      onClick={handleAddTag}
                      sx={{ whiteSpace: 'nowrap', alignSelf: 'flex-start', mt: '6px' }}
                    >
                      Add
                    </Button>
                  </Stack>
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {(form.tags || []).map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        onDelete={() => handleRemoveTag(tag)}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>

              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="overline" sx={{ letterSpacing: 1, color: 'text.secondary' }}>
                Call-to-action Buttons
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Optional buttons appear at the end of the story to guide guests to key in-app destinations (e.g. /tours, /chat).
              </Typography>
              <Stack spacing={2} sx={{ mt: 2 }}>
                {(form.actions || []).map((action, index) => (
                  <Stack
                    key={`action-${index}`}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                  >
                    <TextField
                      fullWidth
                      label="Button Label"
                      placeholder="Book Tour"
                      value={action.label}
                      onChange={(e) => handleActionChange(index, 'label', e.target.value)}
                    />
                    <FormControl fullWidth>
                      <InputLabel>Destination Path</InputLabel>
                      <Select
                        label="Destination Path"
                        value={action.href || ACTION_DESTINATIONS[0]?.href || ''}
                        onChange={(e) => handleActionChange(index, 'href', e.target.value)}
                      >
                        {ACTION_DESTINATIONS.map((dest) => (
                          <MenuItem key={dest.href} value={dest.href}>
                            {dest.label} ({dest.href})
                          </MenuItem>
                        ))}
                        {action.href &&
                        !ACTION_DESTINATIONS.some((dest) => dest.href === action.href) ? (
                          <MenuItem value={action.href}>
                            Custom Path ({action.href})
                          </MenuItem>
                        ) : null}
                      </Select>
                      <FormHelperText>Predefined guest app destinations.</FormHelperText>
                    </FormControl>
                    <Tooltip title="Remove button">
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={() => handleRemoveAction(index)}
                        sx={{ mt: { xs: 0, sm: '4px' } }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ))}
                <Button
                  variant="outlined"
                  onClick={handleAddAction}
                  startIcon={<AddIcon />}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Add Action Button
                </Button>
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="overline" sx={{ letterSpacing: 1, color: 'text.secondary' }}>
                Publishing
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={form.status || 'draft'}
                    onChange={(e) => handleChange('status', e.target.value)}
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="published">Published</MenuItem>
                    <MenuItem value="archived">Archived</MenuItem>
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isFeatured}
                      onChange={(e) => handleChange('isFeatured', e.target.checked)}
                    />
                  }
                  label="Feature on Explore hero"
                />
              </Stack>
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            p: 3,
            borderTop: '1px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Changes are saved to this property only.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="text" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={coverUploading}
              disableElevation
            >
              Save Post
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ExplorePostModal;
